import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import './HintTooltip.scss';

interface HintTooltipProps {
  text: string;
  /** Optional placement relative to the trigger icon (default: 'top') */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Override the default ⓘ glyph with any node (e.g. a react-icons
   *  component). Lets the same hover/tap/dismiss machinery back
   *  badges that aren't info hints — see SubscriberBadge for the
   *  star-icon use case. */
  icon?: React.ReactNode;
}

/**
 * Reusable hint icon (ⓘ) that shows a tooltip on hover (desktop) or
 * tap (mobile). Tapping outside dismisses on mobile.
 *
 * Touch quirk: iOS Safari (and some Android browsers) fire
 * mouseenter → click → mouseleave in a single tap. With the naive
 * "mouseenter shows, mouseleave hides" wiring, the bubble flashed
 * visible for a frame and then immediately hid — looking broken.
 * Gate the hover handlers behind `(hover: hover)` so they only
 * attach on real-mouse devices; touch devices fall through to the
 * tap-toggle / tap-outside path.
 */
const HintTooltip: React.FC<HintTooltipProps> = ({ text, placement = 'top', className, icon }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState(0);

  // Resolve once on mount; touch-vs-mouse capability doesn't change
  // during the page lifetime in practice.
  const canHover = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    [],
  );

  // When the bubble appears, measure it and shift horizontally if it
  // would overflow the viewport. The bubble's CSS centers it under
  // the icon (translateX(-50%)); we add a corrective offset on top so
  // tooltips near the right edge (e.g. the Views stat tile) don't
  // push the page width and create a horizontal scrollbar.
  useLayoutEffect(() => {
    if (!visible) {
      setShift(0);
      return;
    }
    const bubble = bubbleRef.current;
    if (!bubble) return;
    const rect = bubble.getBoundingClientRect();
    const pad = 8;
    if (rect.right > window.innerWidth - pad) {
      setShift(window.innerWidth - pad - rect.right);
    } else if (rect.left < pad) {
      setShift(pad - rect.left);
    } else {
      setShift(0);
    }
  }, [visible]);

  // Dismiss on outside click / tap.
  useEffect(() => {
    if (!visible) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [visible]);

  return (
    <span
      ref={ref}
      className={`hint-tooltip hint-tooltip--${placement}${className ? ` ${className}` : ''}`}
      onMouseEnter={canHover ? () => setVisible(true) : undefined}
      onMouseLeave={canHover ? () => setVisible(false) : undefined}
      // Always show on tap/click. Don't toggle — tap-outside is the
      // close affordance on touch (and Esc / blur, indirectly).
      // Toggling here meant a second tap on the icon would hide the
      // bubble that the first tap just opened, which felt unreliable
      // when the icon is small.
      onClick={(e) => { e.stopPropagation(); setVisible(true); }}
      aria-label={text}
      role="tooltip"
    >
      <span className="hint-icon">{icon ?? 'ⓘ'}</span>
      {visible && (
        <span
          ref={bubbleRef}
          className="hint-bubble"
          style={shift !== 0 ? { transform: `translateX(calc(-50% + ${shift}px))` } : undefined}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default HintTooltip;
